import { useMemo, useState } from "react";
import "../Styles/Poojas.css";
import paymentQR from "../Assests/QRCode.jpeg";
import {
  SearchMoonPhase,
  SearchRiseSet,
  Body,
  Observer,
} from "astronomy-engine";

// ============================================================
// TEMPLE LOCATION
// ============================================================

const TEMPLE_LATITUDE = 10.1486066;
const TEMPLE_LONGITUDE = 76.2287131;
const TEMPLE_TIME_ZONE = "Asia/Kolkata";

const templeObserver = new Observer(
  TEMPLE_LATITUDE,
  TEMPLE_LONGITUDE,
  0
);

// ============================================================
// TODAY
// ============================================================

const getTodayString = () => {
  const today = new Date();

  return new Intl.DateTimeFormat("en-CA", {
    timeZone: TEMPLE_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(today);
};

// ============================================================
// LOCAL DATE PARTS - NORTH PARAVUR / IST
// ============================================================

const getLocalDateParts = (date) => {
  const parts = new Intl.DateTimeFormat("en-IN", {
    timeZone: TEMPLE_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);

  const result = {};

  parts.forEach((part) => {
    if (
      part.type === "year" ||
      part.type === "month" ||
      part.type === "day"
    ) {
      result[part.type] = part.value;
    }
  });

  return result;
};

const getLocalDateString = (date) => {
  const parts = getLocalDateParts(date);

  return (
    `${parts.year}-` +
    `${parts.month}-` +
    `${parts.day}`
  );
};

// ============================================================
// ADD MINUTES
// ============================================================

const addMinutes = (date, minutes) => {
  return new Date(
    date.getTime() + minutes * 60 * 1000
  );
};

// ============================================================
// FORMAT TIME - IST
// ============================================================

const formatTime = (date) => {
  return new Intl.DateTimeFormat("en-IN", {
    timeZone: TEMPLE_TIME_ZONE,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(date);
};

// ============================================================
// FORMAT DISPLAY DATE
// ============================================================

const formatDisplayDate = (
  dateString,
  longMonth = false
) => {
  if (!dateString) {
    return "";
  }

  return new Date(
    `${dateString}T00:00:00+05:30`
  ).toLocaleDateString("en-IN", {
    timeZone: TEMPLE_TIME_ZONE,
    day: "numeric",
    month: longMonth ? "long" : "short",
  });
};

// ============================================================
// GET SUNSET FOR A PARTICULAR LOCAL DATE
// ============================================================

const getSunsetForDate = (dateString) => {
  const localMidnight = new Date(
    `${dateString}T00:00:00+05:30`
  );

  const result = SearchRiseSet(
    Body.Sun,
    templeObserver,
    -1,
    addMinutes(localMidnight, 12 * 60),
    1
  );

  return result ? result.date : null;
};

// ============================================================
// GET SUNRISE FOR NEXT LOCAL DATE
// ============================================================

const getNextSunrise = (dateString) => {
  const localMidnight = new Date(
    `${dateString}T00:00:00+05:30`
  );

  const result = SearchRiseSet(
    Body.Sun,
    templeObserver,
    +1,
    addMinutes(localMidnight, 24 * 60),
    1
  );

  return result ? result.date : null;
};

// ============================================================
// CHECK WHETHER AMAVASAI TITHI PREVAILS DURING NIGHT
// ============================================================

const tithiPrevailsDuringNight = (
  tithiStart,
  tithiEnd,
  localDateString
) => {
  const sunset =
    getSunsetForDate(localDateString);

  const nextSunrise =
    getNextSunrise(localDateString);

  if (!sunset || !nextSunrise) {
    return false;
  }

  return (
    tithiStart < nextSunrise &&
    tithiEnd > sunset
  );
};

// ============================================================
// FIND AMAVASAI TITHI DATA
// ============================================================

const calculateAmavasiData = (year) => {
  const results = [];

  // Extended window to capture month transitions seamlessly
  let searchDate = new Date(
    `${year - 1}-12-01T00:00:00Z`
  );

  const searchEnd = new Date(
    `${year + 1}-02-01T00:00:00Z`
  );

  let safetyCounter = 0;

  while (
    searchDate < searchEnd &&
    safetyCounter < 30
  ) {
    safetyCounter++;

    // --------------------------------------------------------
    // AMAVASAI TITHI START (348°)
    // --------------------------------------------------------

    const startPhase =
      SearchMoonPhase(
        348,
        searchDate,
        40
      );

    if (!startPhase) {
      break;
    }

    const tithiStart =
      startPhase.date;

    // --------------------------------------------------------
    // AMAVASAI TITHI END (0°)
    // --------------------------------------------------------

    const searchAfterStart =
      addMinutes(
        tithiStart,
        2
      );

    // Increased search window from 5 to 15 days to ensure phase match
    const endPhase =
      SearchMoonPhase(
        0,
        searchAfterStart,
        15
      );

    if (!endPhase) {
      break;
    }

    const tithiEnd =
      endPhase.date;

    const startDate =
      getLocalDateString(
        tithiStart
      );

    const endDate =
      getLocalDateString(
        tithiEnd
      );

    // --------------------------------------------------------
    // FIND THE LOCAL DATE ON WHICH AMAVASAI PREVAILS AT NIGHT
    // --------------------------------------------------------

    const candidateDates = [];

    const startLocal =
      new Date(
        `${startDate}T00:00:00+05:30`
      );

    for (
      let i = -1;
      i <= 2;
      i++
    ) {
      const candidate =
        new Date(
          startLocal.getTime() +
            i * 24 * 60 * 60 * 1000
        );

      candidateDates.push(
        getLocalDateString(candidate)
      );
    }

    let poojaDate = null;

    for (
      const candidateDate
      of candidateDates
    ) {
      if (
        tithiPrevailsDuringNight(
          tithiStart,
          tithiEnd,
          candidateDate
        )
      ) {
        poojaDate =
          candidateDate;

        break;
      }
    }

    if (!poojaDate) {
      poojaDate =
        startDate;
    }

    // Keep all entries to allow cross-year/month queries
    results.push({
      startDate,
      endDate,

      startTime:
        formatTime(
          tithiStart
        ),

      endTime:
        formatTime(
          tithiEnd
        ),

      poojaDate,
    });

    // Move forward past the end phase
    searchDate =
      addMinutes(
        tithiEnd,
        120
      );
  }

  return results;
};

// ============================================================
// GET NEXT UPCOMING GURUTHY DATE
// ============================================================

const getNextUpcomingGuruthyDate = (
  data
) => {
  const today =
    getTodayString();

  const dates =
    data
      .map(
        (item) =>
          item.poojaDate
      )
      .filter(
        (date) =>
          date >= today
      )
      .sort();

  return dates.length
    ? dates[0]
    : null;
};

// ============================================================
// INITIAL CALENDAR DATE
// ============================================================

const getInitialCalendarDate = () => {
  const today =
    new Date();

  const localParts =
    getLocalDateParts(
      today
    );

  const year =
    Number(
      localParts.year
    );

  const data =
    calculateAmavasiData(
      year
    );

  const nextDate =
    getNextUpcomingGuruthyDate(
      data
    );

  if (nextDate) {
    const [
      nextYear,
      nextMonth,
    ] =
      nextDate
        .split("-")
        .map(Number);

    return {
      month:
        nextMonth - 1,
      year:
        nextYear,
    };
  }

  return {
    month:
      today.getMonth(),
    year,
  };
};

// ============================================================
// POOJAS COMPONENT
// ============================================================

function Poojas() {

  // ==========================================================
  // POOJA DATA
  // ==========================================================



  // ==========================================================
  // BOOKING STATE
   
  // ==========================================================

  const [showBooking, setShowBooking] =
    useState(false);

  const [showPayment, setShowPayment] =
    useState(false);

  const [formData, setFormData] =
    useState({
      name: "",
      address: "",
      phone: "",
    });

  // ==========================================================
  // INITIAL CALENDAR
  // ==========================================================

  const initialCalendarDate =
    useMemo(
      () =>
        getInitialCalendarDate(),
      []
    );

  const [
    currentMonth,
    setCurrentMonth,
  ] = useState(
    initialCalendarDate.month
  );

  const [
    currentYear,
    setCurrentYear,
  ] = useState(
    initialCalendarDate.year
  );

  // ==========================================================
  // DYNAMIC AMAVASI DATA
  // ==========================================================

  const amavasiData =
    useMemo(
      () =>
        calculateAmavasiData(
          currentYear
        ),
      [currentYear]
    );

  // ==========================================================
  // GURUTHY DATES
  // ==========================================================

  const guruthyDates =
    amavasiData.map(
      (item) =>
        item.poojaDate
    );

  // ==========================================================
  // MONTH NAMES
  // ==========================================================

  const monthNames =
    Array.from(
      { length: 12 },
      (_, index) =>
        new Intl.DateTimeFormat(
          "en-IN",
          {
            month: "long",
          }
        ).format(
          new Date(
            2026,
            index,
            1
          )
        )
    );

  // ==========================================================
  // WEEK DAYS
  // ==========================================================

  const weekDays =
    Array.from(
      { length: 7 },
      (_, index) =>
        new Intl.DateTimeFormat(
          "en-IN",
          {
            weekday: "short",
          }
        ).format(
          new Date(
            2026,
            0,
            4 + index
          )
        )
    );

  // ==========================================================
  // FORM CHANGE
  // ==========================================================

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]:
        e.target.value,
    });
  };

  // ==========================================================
  // BOOK NOW
  // ==========================================================

  const handleBookNow = () => {
    setShowBooking(true);
  };

  // ==========================================================
  // CANCEL
  // ==========================================================

  const handleCancel = () => {
    setShowBooking(false);
    setShowPayment(false);

    setFormData({
      name: "",
      address: "",
      phone: "",
    });
  };

  // ==========================================================
  // PAYMENT
  // ==========================================================

  const handlePayment = () => {

    if (!formData.name.trim()) {
      alert(
        "Please enter your name."
      );
      return;
    }

    if (!formData.address.trim()) {
      alert(
        "Please enter your address."
      );
      return;
    }

    if (!formData.phone.trim()) {
      alert(
        "Please enter your phone number."
      );
      return;
    }

    if (
      formData.phone.length !== 10
    ) {
      alert(
        "Please enter a valid 10 digit phone number."
      );
      return;
    }

    setShowPayment(true);
  };

  // ==========================================================
  // CALENDAR DAYS
  // ==========================================================

  const getCalendarDays = () => {

    const firstDay =
      new Date(
        currentYear,
        currentMonth,
        1
      ).getDay();

    const totalDays =
      new Date(
        currentYear,
        currentMonth + 1,
        0
      ).getDate();

    const days = [];

    for (
      let i = 0;
      i < firstDay;
      i++
    ) {
      days.push(null);
    }

    for (
      let day = 1;
      day <= totalDays;
      day++
    ) {
      days.push(day);
    }

    return days;
  };

  const calendarDays =
    getCalendarDays();

  // ==========================================================
  // PREVIOUS MONTH
  // ==========================================================

  const previousMonth = () => {

    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(
        currentYear - 1
      );
    } else {
      setCurrentMonth(
        currentMonth - 1
      );
    }
  };

  // ==========================================================
  // NEXT MONTH
  // ==========================================================

  const nextMonth = () => {

    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(
        currentYear + 1
      );
    } else {
      setCurrentMonth(
        currentMonth + 1
      );
    }
  };

  // ==========================================================
  // CURRENT MONTH AMAVASI
  // ==========================================================

  const currentAmavasi =
    amavasiData.find(
      (item) => {

        const [
          year,
          month,
        ] =
          item.poojaDate
            .split("-")
            .map(Number);

        return (
          year === currentYear &&
          month ===
            currentMonth + 1
        );
      }
    );

  // ==========================================================
  // CHECK GURUTHY DATE
  // ==========================================================

  const isGuruthyDate = (
    day
  ) => {

    if (!day) {
      return false;
    }

    const date =
      `${currentYear}-` +
      `${String(
        currentMonth + 1
      ).padStart(2, "0")}-` +
      `${String(day).padStart(
        2,
        "0"
      )}`;

    return guruthyDates.includes(
      date
    );
  };

  // ==========================================================
  // TITHI TIMING TEXT
  // ==========================================================

  const getTithiTimingText = () => {

    if (!currentAmavasi) {
      return "Tithi timing not available";
    }

    const startDate =
      formatDisplayDate(
        currentAmavasi.startDate
      );

    const endDate =
      formatDisplayDate(
        currentAmavasi.endDate
      );

    return (
      `Tithi: ${startDate}, ` +
      `${currentAmavasi.startTime} – ` +
      `${endDate}, ` +
      `${currentAmavasi.endTime}`
    );
  };

  // ==========================================================
  // CLICK GURUTHY DATE
  // ==========================================================

  const showGuruthyDetails = (
    day
  ) => {

    const date =
      `${currentYear}-` +
      `${String(
        currentMonth + 1
      ).padStart(2, "0")}-` +
      `${String(day).padStart(
        2,
        "0"
      )}`;

    const formattedDate =
      new Date(
        `${date}T00:00:00+05:30`
      ).toLocaleDateString(
        "en-IN",
        {
          timeZone:
            TEMPLE_TIME_ZONE,
          day: "numeric",
          month: "long",
          year: "numeric",
        }
      );

    const selectedAmavasi =
      amavasiData.find(
        (item) =>
          item.poojaDate ===
          date
      );

    let tithiInfo = "";

    if (selectedAmavasi) {

      tithiInfo =
        `\n\nTithi: ` +
        `${formatDisplayDate(
          selectedAmavasi.startDate
        )}, ` +
        `${selectedAmavasi.startTime} – ` +
        `${formatDisplayDate(
          selectedAmavasi.endDate
        )}, ` +
        `${selectedAmavasi.endTime}`;
    }

    alert(
      `🌑 Guruthy Pooja\n\n` +
      `Amavasai\n` +
      `${formattedDate}` +
      tithiInfo +
      `\n\n` +
      `Tithi Prevails at Night\n\n` +
      `Special Guruthy Pooja will be conducted on this day.`
    );
  };

  // ==========================================================
  // NEXT GURUTHY DATE
  // ==========================================================

  const nextGuruthyDate =
    getNextUpcomingGuruthyDate(
      amavasiData
    );

  const formattedNextDate =
    nextGuruthyDate
      ? new Date(
          `${nextGuruthyDate}T00:00:00+05:30`
        ).toLocaleDateString(
          "en-IN",
          {
            timeZone:
              TEMPLE_TIME_ZONE,
            day: "numeric",
            month: "long",
            year: "numeric",
          }
        )
      : "Coming Soon";

  return (
    <div className="poojas-page">

      <section className="pooja-calendar-section">

        <div className="pooja-calendar-wrapper">

          <div className="pooja-calendar">

            <div className="pooja-calendar-header">

              <button
                type="button"
                onClick={
                  previousMonth
                }
              >
                ❮
              </button>

              <h2>
                {
                  monthNames[
                    currentMonth
                  ]
                }{" "}
                {currentYear}
              </h2>

              <button
                type="button"
                onClick={
                  nextMonth
                }
              >
                ❯
              </button>

            </div>

            <div className="pooja-calendar-weekdays">

              {weekDays.map(
                (day) => (
                  <div key={day}>
                    {day}
                  </div>
                )
              )}

            </div>

            <div className="pooja-calendar-days">

              {calendarDays.map(
                (
                  day,
                  index
                ) => {

                  if (
                    day === null
                  ) {
                    return (
                      <div
                        key={index}
                        className="pooja-calendar-day empty"
                      />
                    );
                  }

                  const guruthy =
                    isGuruthyDate(
                      day
                    );

                  return (
                    <div
                      key={index}
                      className={
                        guruthy
                          ? "pooja-calendar-day guruthy"
                          : "pooja-calendar-day"
                      }
                      onClick={() =>
                        guruthy &&
                        showGuruthyDetails(
                          day
                        )
                      }
                    >

                      {guruthy ? (

                        <span className="pooja-moon-date">

                          <span>
                            🌑
                          </span>

                          <small>
                            {day}
                          </small>

                        </span>

                      ) : (
                        day
                      )}

                    </div>
                  );
                }
              )}

            </div>

            <div className="pooja-calendar-legend">

              <div className="your-div">

                <div className="amavasi-title-line">
                  🌑 Amavasai - Guruthy Pooja
                </div>

                <div className="amavasi-tithi-line">
                  {
                    getTithiTimingText()
                  }
                </div>

                <div className="amavasi-night-line">
                  “Pooja performed when Tithi prevails at Night.”
                </div>

              </div>

            </div>

          </div>

          <div className="pooja-special-details">

            <div className="special-moon">
              🌑
            </div>

            <h1>
              Guruthy Pooja
            </h1>

            <h3>
              Every Amavasi / Karuthavavu
            </h3>

            <p className="special-description">
              Join us for the sacred
              Guruthy Pooja performed
              on Amavasi.
            </p>

            <div className="next-guruthy-section">

              <div className="next-title">
                NEXT GURUTHY POOJA
              </div>

              <div className="next-guruthy-date">
                {
                  formattedNextDate
                }
              </div>

              <div className="next-amavasi">
                🌑 Amavasi
              </div>

            </div>

            <p className="highlight-note">
              🌑 Highlighted dates indicate
              Guruthy Pooja.
            </p>

            <p className="click-note">
              Click on a highlighted
              Amavasi date for details.
            </p>

          </div>

        </div>

      </section>


      <section>

        
      </section>

      <section className="pooja-contact-booking">

        <div className="pooja-booking-container">

          <div className="pooja-booking-info">

            <div className="pooja-booking-icon">
              🪔
            </div>

            <div className="pooja-booking-content">

              <h2>
                Guruthy Pooja Booking
              </h2>

              <p>
                For Guruthy Pooja booking and
                further details, please contact
                the temple committee.
              </p>

              <div className="contact-details">

                <div className="contact-item">

                  <strong>
                    Contact Person
                  </strong>

                  <span>
                    
                    Madhu P.

                  </span>
                   <span>
                  
Sanalkumar M.C 
 </span>

 <span>
     Santhoshkumar V.N 
 </span>


                </div>

                <div className="contact-item">

                  <strong>
                    Phone
                  </strong>

                  <a href="tel:9895761050">
                    9388635662
                  </a>
<a href="tel:9895761050">
                    9447391224
                  </a><a href="tel:9895761050">
                    9961737313
                  </a>
                </div>

              </div>

            </div>

          </div>

          <div className="pooja-booking-action">

            {!showBooking && (

              <button
                type="button"
                className="btn book-btn"
                onClick={
                  handleBookNow
                }
              >
                🪔 Book Now
              </button>

            )}

            {showBooking && (

              <div className="booking-form">

                <h3>
                  Devotee Details
                </h3>

                <div className="form-group">

                  <label>
                    Name
                  </label>

                  <input
                    type="text"
                    name="name"
                    placeholder="Enter your name"
                    value={
                      formData.name
                    }
                    onChange={
                      handleChange
                    }
                  />

                </div>

                <div className="form-group">

                  <label>
                    Address
                  </label>

                  <textarea
                    name="address"
                    placeholder="Enter your address"
                    rows="3"
                    value={
                      formData.address
                    }
                    onChange={
                      handleChange
                    }
                  />

                </div>

                <div className="form-group">

                  <label>
                    Phone Number
                  </label>

                  <input
                    type="tel"
                    name="phone"
                    placeholder="Enter 10 digit phone number"
                    maxLength="10"
                    value={
                      formData.phone
                    }
                    onChange={
                      handleChange
                    }
                  />

                </div>

                <button
                  type="button"
                  className="pay-btn"
                  onClick={
                    handlePayment
                  }
                >
                  💳 Proceed to Payment
                </button>

                {showPayment && (

                  <div className="payment-box">

                    <h3>
                      Complete Payment
                    </h3>

                    <p className="payment-pooja">
                      Guruthy Pooja
                    </p>

                    <p className="payment-amount">
                      Amount:{" "}
                      <strong>
                        ₹501
                      </strong>
                    </p>

                    <div className="qr-container">

                      <img
                        src={paymentQR}
                        alt="Guruthy Pooja Payment QR Code"
                        className="temple-qr"
                      />

                      <h4>
                        Nambooriyachan All Kshethra Seva Samithy
                      </h4>

                      <p>
                        Scan the QR code using any UPI App
                      </p>

                    </div>

                    <div className="upi-details">

                      <span>
                        UPI ID
                      </span>

                      <strong>
                        boim-856473137841@boi
                      </strong>

                    </div>

                    <p className="payment-note">
                      Please complete the payment of ₹501 and
                      keep the payment confirmation for your records.
                    </p>

                  </div>

                )}

                <button
                  type="button"
                  className="cancel-btn"
                  onClick={
                    handleCancel
                  }
                >
                  Cancel
                </button>

              </div>

            )}

          </div>

        </div>

      </section>
      

      <section className="instructions">

        <div className="container">

          <h2>
            Booking Instructions
          </h2>

          <ul>

            <li>
              ✔ Book your pooja at least one day in advance.
            </li>

            <li>
              ✔ Carry the booking confirmation during your visit.
            </li>

            <li>
              ✔ Online payment facility will be available.
            </li>

            <li>
              ✔ Temple authorities reserve the right to modify timings.
            </li>

          </ul>

        </div>

      </section>

    </div>
  );

  
}

export default Poojas;